/**
 * White-label branding for the web app.
 *
 * Everything a viewer can read that names the product lives here, sourced from Vite environment
 * variables so a deployment can be re-skinned without touching a component. The values are baked in
 * at BUILD time (that is how Vite works), so a re-brand means a rebuild — which is the right
 * trade-off for a self-hosted product: no runtime lookup, nothing to configure at boot.
 *
 * Set these in `apps/web/.env` or on the build command:
 *
 *   VITE_BRAND_NAME="Acme Logistics"
 *   VITE_BRAND_LEGAL_NAME="Acme Logistics Pvt Ltd"
 *   VITE_BRAND_TAGLINE="Logistics that moves with you."
 *   VITE_BRAND_BLURB="B2B + B2C courier management …"
 *
 * There is deliberately no vendor name anywhere in the defaults: this codebase is sold on, and the
 * buyer's customers should never see who wrote it.
 */
const env = import.meta.env as Record<string, string | undefined>;

export const BRAND = {
  /** Short product name — sidebar, page titles, login header. */
  name: env.VITE_BRAND_NAME || 'ShipRoute',
  /** Registered entity — invoices, footers, legal text. */
  legalName: env.VITE_BRAND_LEGAL_NAME || 'ShipRoute Logistics Pvt Ltd',
  tagline: env.VITE_BRAND_TAGLINE || 'Route Through AI',
  /**
   * The large statement on the login panel. Deliberately NOT the tagline: the tagline sits in the
   * wordmark lockup at the top of the same panel, and printing it twice reads as a mistake.
   */
  headline: env.VITE_BRAND_HEADLINE || 'Every shipment, every carrier, one platform.',
  blurb:
    env.VITE_BRAND_BLURB ||
    'AI-assisted courier management — smart carrier routing, live tracking, NDR, invoicing and COD remittance in one platform.',
  /** Rendered as "© <year> <copyright>". */
  copyright: env.VITE_BRAND_COPYRIGHT || env.VITE_BRAND_LEGAL_NAME || 'ShipRoute Logistics Pvt Ltd',
} as const;
